import startsWith from 'lodash/startsWith';

import { GenericDataType } from 'modules/domains';
import { Field } from 'modules/workflowNodeEditors/components/fieldsMatchEditor/FieldsMatchQueryBuilder.utils';
import { ESTypeMap } from 'modules/workflows/types';
import {
  ALLOWED_FIELD_TYPES,
  ALLOWED_PROPERTIES,
  EXCLUDED_FIELDS,
  EXCLUDED_OBJECTS,
  EXCLUDED_USER_FIELDS,
  EXCLUDED_USER_OBJECTS,
} from './CriteriaQueryBuilder.constants';

/**
 * Returns only the mappings that are needed for the CriteriaQueryBuilder
 * @param mappings -- the mappings returned from Elasticsearch
 * @returns Only the mappings that are needed for the CriteriaQueryBuilder
 */
export const getIncludedMappings = (mappings: ESTypeMap) => {
  const includedMappings: ESTypeMap = {};
  ALLOWED_PROPERTIES.forEach((property) => {
    includedMappings[property] = mappings[property];
  });

  return includedMappings;
};

/**
 * Checks if the field is the same as the comparing field
 * @param field - the field to test
 * @param comparingField - the field to compare against
 * @returns `true` if the field is the same as the comparing field, `false` otherwise
 */
const isField = (field: string, comparingField: string) => field === comparingField;

/**
 * Checks if the field is part of a particular object
 * NOTE: it will return true for either a top level field or a nested field
 *
 * @param field - the field to test
 * @param objectName - the name of the object to compare against
 * @returns `true` if the field is part of the object, `false` otherwise
 */
const isFieldPartOfObject = (field: string, objectName: string) =>
  isField(field, objectName) || startsWith(field, `${objectName}.`);

/**
 * Checks if the field should be excluded
 * @param field - the field to test
 * @param excludedFields - list of excluded field names
 * @returns `true` if the field is excluded, `false` otherwise
 */
const isFieldExcluded = (field: string, excludedFields: (string | RegExp)[]) =>
  excludedFields.some((excludedField) =>
    excludedField instanceof RegExp ? excludedField.test(field) : excludedField === field,
  );

/**
 * Checks if the object should be excluded
 * NOTE: it will return true for either a top level field or a nested field
 * @param field - the field to test
 * @param excludedObjects - list of excluded object names
 * @returns `true` if the field is part of an excluded object, `false` otherwise
 */
const isObjectExcluded = (field: string, excludedObjects: string[]) =>
  excludedObjects.some((excludedObject) => isFieldPartOfObject(field, excludedObject));

/**
 * Filters out fields that AUT does not support so that they are not shown in
 * the criteria builder
 * @param propertyName - property name being checked (e.g. User, Purchase, CustomEvent)
 * @param item - the field to check
 * @returns `true` if the field should be included, `false` otherwise
 */
export const filterMappings = (propertyName: string, item: Field) => {
  const { field, fieldType } = item;

  if (!ALLOWED_FIELD_TYPES.includes(fieldType)) return false;
  if (EXCLUDED_FIELDS.includes(field)) return false;
  if (isObjectExcluded(field, EXCLUDED_OBJECTS)) return false;
  if (propertyName === GenericDataType.User) {
    if (isFieldExcluded(field, EXCLUDED_USER_FIELDS)) return false;
    if (isObjectExcluded(field, EXCLUDED_USER_OBJECTS)) return false;
  }
  return true;
};
